/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { hasURLProtocol } from '@e180/util/lib/url';

import usePlatformURL from 'src/shared/app/base/hook/usePlatformURL';
import useReduxState from 'src/shared/app/base/hook/useReduxState';
import useRouteConfig from 'src/shared/app/base/route/hook/useRouteConfig';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { getRoutes } from 'src/shared/app/base/route/registry/routeRegistry';
import { redirectQueryParam } from 'src/shared/app/base/route/setting/querySettings';
import { homeRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { getRouteRedirect } from 'src/shared/app/base/route/util/routeUtils';
import useAuthenticationToken from 'src/shared/app/authentication/hook/useAuthenticationToken';

const RedirectSwitch = (): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const state = useReduxState();
  const token = useAuthenticationToken();
  const platformURL = usePlatformURL();
  const config = useRouteConfig();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Switch>
      {getRoutes().map((route) => {
        // @TODO - really bad use of hook
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [homePath, path] = useRoutes(homeRoute, route);

        return (
          <Route
            path={path}
            exact
            render={({ location }) => {
              const redirectPath = getRouteRedirect(route, state, config);

              if (!redirectPath) {
                return null;
              }

              console.log('Redirection to', redirectPath);

              const { search, pathname } = location;

              // If there is a URL protocol, we have an absolute URI
              if (hasURLProtocol(redirectPath)) {
                // If we are in the browser, a simple javascript redirection will do
                if (typeof window !== 'undefined') {
                  window.location = redirectPath;

                  // Prevent error screen to be displayed while redirecting occurs
                  return null;
                }

                // Otherwise we do a server redirection
                return <Redirect to={redirectPath} />;
              }

              // Propagate query arguments with the redirection. A base is required,
              // otherwise an InvalidURL exception is thrown.
              const url = new URL(redirectPath + search, platformURL);

              // If user is not authenticated, add the current path to the
              // `redirect` query param. Skip this step if the current path is the
              // home path, since after login the user will be redirected there
              // by default
              if (!token && pathname !== homePath) {
                url.searchParams.append(redirectQueryParam, pathname);
              }

              // Get rid of the URL base required above by the URL constructore
              return <Redirect to={url.href.replace(url.origin, '')} />;
            }}
            key={path}
          />
        );
      })}
    </Switch>
  );
};

export default compose(React.memo)(RedirectSwitch);
